import React, { useState, useEffect } from 'react'
import { 
  Box, 
  VStack, 
  HStack, 
  Text, 
  Button, 
  Input, 
  FormControl, 
  FormLabel, 
  FormErrorMessage,
  Select,
  Checkbox,
  SimpleGrid,
  IconButton,
  useColorModeValue,
  Divider,
  Progress,
  Icon,
  useToast
} from '@chakra-ui/react'
import { FaArrowLeft, FaCheck, FaTimes, FaEye, FaEyeSlash } from 'react-icons/fa'
import { useAuthContext } from '../../contexts/AuthContext'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { UserRegistrationForm, SubscriptionPlan, PromoCode, AdminSettings } from '../../types/user'
import { pricingService, promoCodeService, adminService } from '../../services/firestore'

interface RegisterFormProps {
  onSuccess?: () => void
  onSwitchToLogin?: () => void
}

// Common passwords to check against
const COMMON_PASSWORDS = [
  'password', '123456', '123456789', 'qwerty', 'abc123', 'password123',
  'admin', 'letmein', 'welcome', 'monkey', 'dragon', 'master', 'sunshine',
  'princess', 'qwerty123', 'admin123', 'password1', '12345678', 'baseball',
  'football', 'superman', 'trustno1', 'butterfly', 'dragon123', 'master123'
]

interface PasswordRequirement {
  label: string
  test: (password: string) => boolean
  met: boolean
}

export const RegisterForm: React.FC<RegisterFormProps> = ({ onSuccess, onSwitchToLogin }) => {
  const { register, error, loading, clearError } = useAuthContext()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const toast = useToast()
  const [pricingPlans, setPricingPlans] = useState<SubscriptionPlan[]>([])
  const [adminSettings, setAdminSettings] = useState<AdminSettings | null>(null)
  const [isLoadingPricing, setIsLoadingPricing] = useState(true)
  const [promoCode, setPromoCode] = useState('')
  const [appliedPromoCode, setAppliedPromoCode] = useState<PromoCode | null>(null)
  const [isValidatingPromoCode, setIsValidatingPromoCode] = useState(false)
  const [formData, setFormData] = useState<UserRegistrationForm>({
    email: '',
    password: '',
    displayName: '',
    phoneNumber: '',
    companyName: '',
    vatNumber: '',
    address: {
      street: '',
      city: '',
      postalCode: '',
      country: 'ZA'
    },
    planType: '',
    courts: 1
  })
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showVatFields, setShowVatFields] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  
  const cardBg = useColorModeValue('#191919', '#191919')
  const borderColor = useColorModeValue('gray.700', 'gray.700')
  const textColor = useColorModeValue('white', 'white')
  const labelColor = useColorModeValue('gray.300', 'gray.300')
  const inputBg = useColorModeValue('#191919', '#191919')
  const inputBorder = useColorModeValue('gray.600', 'gray.600')
  const placeholderColor = useColorModeValue('gray.400', 'gray.400')

  // OWASP Password Requirements
  const passwordRequirements: PasswordRequirement[] = [
    {
      label: 'At least 8 characters long',
      test: (password: string) => password.length >= 8,
      met: false
    },
    {
      label: 'Contains at least one uppercase letter (A-Z)',
      test: (password: string) => /[A-Z]/.test(password),
      met: false
    },
    {
      label: 'Contains at least one lowercase letter (a-z)',
      test: (password: string) => /[a-z]/.test(password),
      met: false
    },
    {
      label: 'Contains at least one number (0-9)',
      test: (password: string) => /\d/.test(password),
      met: false
    },
    {
      label: 'Contains at least one special character (!@#$%^&*)',
      test: (password: string) => /[!@#$%^&*(),.?":{}|<>]/.test(password),
      met: false
    },
    {
      label: 'Not a common password',
      test: (password: string) => !COMMON_PASSWORDS.includes(password.toLowerCase()),
      met: false
    },
    {
      label: 'No sequential characters (123, abc)',
      test: (password: string) => {
        const lower = password.toLowerCase()
        for (let i = 0; i < lower.length - 2; i++) {
          const seq1 = lower.charCodeAt(i + 1) - lower.charCodeAt(i)
          const seq2 = lower.charCodeAt(i + 2) - lower.charCodeAt(i + 1)
          if (seq1 === 1 && seq2 === 1) return false
        }
        return true
      },
      met: false
    },
    {
      label: 'No repeated characters (aaa, 111)',
      test: (password: string) => {
        for (let i = 0; i < password.length - 2; i++) {
          if (password[i] === password[i + 1] && password[i] === password[i + 2]) {
            return false
          }
        }
        return true
      },
      met: false
    }
  ]

  // Calculate password strength
  const calculatePasswordStrength = (password: string): number => {
    if (!password) return 0
    
    let strength = 0
    passwordRequirements.forEach(req => {
      if (req.test(password)) strength += 12.5 // 100% / 8 requirements
    })
    
    return Math.min(100, strength)
  }

  // Get password strength color
  const getPasswordStrengthColor = (strength: number): string => {
    if (strength < 25) return 'red.400'
    if (strength < 50) return 'orange.400'
    if (strength < 75) return 'yellow.400'
    return 'green.400'
  }

  // Get password strength label
  const getPasswordStrengthLabel = (strength: number): string => {
    if (strength < 25) return 'Very Weak'
    if (strength < 50) return 'Weak'
    if (strength < 75) return 'Moderate'
    if (strength < 100) return 'Strong'
    return 'Very Strong'
  }

  const handleInputChange = (field: string, value: string) => {
    clearError()
    if (field.includes('.')) {
      const [parent, child] = field.split('.')
      if (parent === 'address') {
        setFormData(prev => ({
          ...prev,
          address: {
            ...prev.address,
            [child]: value
          }
        }))
      }
    } else if (field === 'courts') {
      setFormData(prev => ({
        ...prev,
        [field]: parseInt(value) || 1
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }))
    }
  }

  const handlePromoCodeValidation = async () => {
    if (!promoCode.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter a promo code',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    setIsValidatingPromoCode(true)
    try {
      const promo = await promoCodeService.getPromoCodeByCode(promoCode.trim())
      if (promo) {
        // Check if promo code has reached max uses
        if (promo.maxUses && promo.usedCount >= promo.maxUses) {
          toast({
            title: 'Promo Code Expired',
            description: 'This promo code has reached its maximum usage limit',
            status: 'error',
            duration: 5000,
            isClosable: true,
          })
          setAppliedPromoCode(null)
          return
        }
        
        // Find the discounted version of the current plan
        const currentPlanId = localStorage.getItem('registrationPlanId')
        if (currentPlanId) {
          const allPlans = await pricingService.getPricingPlans()
          const discountPlan = allPlans.find(plan => 
            plan.originalPlanId === currentPlanId && plan.isDiscountVersion === true
          )
          
          if (discountPlan) {
            // Store the original plan name before updating to discounted version
            const originalPlanName = localStorage.getItem('registrationOriginalPlanName') || localStorage.getItem('registrationPlanName') || '';
            
            // Update localStorage with the discounted plan information
            localStorage.setItem('registrationPlanId', discountPlan.id || '')
            // Use the plan name from database (it already includes "(DISCOUNTED)")
            localStorage.setItem('registrationPlanName', discountPlan.name || '')
            // Preserve the original plan name (non-discounted version)
            localStorage.setItem('registrationOriginalPlanName', originalPlanName)
          }
        }
        
        setAppliedPromoCode(promo)
        toast({
          title: 'Promo Code Applied!',
          description: `${promo.discountPercentage}% discount applied successfully`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        })
      } else {
        setAppliedPromoCode(null)
        toast({
          title: 'Invalid Promo Code',
          description: 'The promo code you entered is not valid',
          status: 'error',
          duration: 3000,
          isClosable: true,
        })
      }
    } catch (error) {
      console.error('Error validating promo code:', error)
      toast({
        title: 'Error',
        description: 'Failed to validate promo code',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    } finally {
      setIsValidatingPromoCode(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    clearError()
    
    if (!validateForm()) {
      return
    }
    
    try {
      await register(formData)
      
      // Get the selected plan details
      const selectedPlan = formData.planType ? pricingPlans.find(plan => {
        return plan.type === formData.planType && 
               (formData.planType === 'individual' || plan.courts === formData.courts)
      }) : null
      
      // Store plan information in localStorage
      if (selectedPlan) {
        // Only update plan ID and name if no promo code has been applied
        // (promo code validation already updated these with discounted versions)
        if (!appliedPromoCode) {
          localStorage.setItem('registrationPlanId', selectedPlan.id || '')
          localStorage.setItem('registrationPlanName', selectedPlan.name || '')
        }
        // Only set original plan name if it doesn't exist yet (preserve original non-discounted name)
        if (!localStorage.getItem('registrationOriginalPlanName')) {
          localStorage.setItem('registrationOriginalPlanName', selectedPlan.name || '')
        }
        // Store the total amount (per-court price × number of courts for club plans)
        const totalAmount = selectedPlan.type === 'club' && selectedPlan.courts 
          ? selectedPlan.price * selectedPlan.courts 
          : selectedPlan.price
        localStorage.setItem('registrationOriginalAmount', totalAmount.toString())
      }
      
      // Store promo code information in localStorage if applied
      if (appliedPromoCode) {
        localStorage.setItem('registrationPromoCode', appliedPromoCode.code)
        localStorage.setItem('registrationPromoCodeId', appliedPromoCode.id || '')
        localStorage.setItem('registrationDiscountPercentage', appliedPromoCode.discountPercentage.toString())
      }
      
      // Redirect to 3-digit code verification page
      navigate(`/verify-code?email=${encodeURIComponent(formData.email)}`)
    } catch (error) {
      // Error is handled by the auth hook
    }
  }

  const validateForm = () => {
    if (formData.password !== confirmPassword) return false
    if (calculatePasswordStrength(formData.password) < 75) return false // Require at least 75% strength
    if (!formData.email || !formData.displayName) return false
    if (!formData.planType) return false // Require plan selection
    if (formData.planType === 'club' && (!formData.courts || formData.courts < 1)) return false
    return true
  }

  // Update password requirements based on current password
  const currentPasswordRequirements = passwordRequirements.map(req => ({
    ...req,
    met: req.test(formData.password)
  }))

  // Load pricing plans on component mount
  useEffect(() => {
    const loadPricingPlans = async () => {
      try {
        const plans = await pricingService.getPricingPlans()
        setPricingPlans(plans)
      } catch (error) {
        console.error('Error loading pricing plans:', error)
        toast({
          title: 'Error',
          description: 'Failed to load pricing information',
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
      } finally {
        setIsLoadingPricing(false)
      }
    }

    loadPricingPlans()
  }, [toast])

  // Handle URL parameters for pre-selecting plans
  useEffect(() => {
    const planType = searchParams.get('plan')
    const courts = searchParams.get('courts')
    
    if (planType && (planType === 'individual' || planType === 'club')) {
      setFormData(prev => ({
        ...prev,
        planType: planType as 'individual' | 'club'
      }))
    }
    
    if (courts && planType === 'club') {
      const courtsCount = parseInt(courts)
      if (!isNaN(courtsCount) && courtsCount > 0) {
        setFormData(prev => ({
          ...prev,
          courts: courtsCount
        }))
      }
    }
  }, [searchParams])

  // Load admin settings on component mount
  useEffect(() => {
    const loadAdminSettings = async () => {
      try {
        const settings = await adminService.getAdminSettings()
        setAdminSettings(settings)
      } catch (error) {
        console.error('Error loading admin settings:', error)
        toast({
          title: 'Error',
          description: 'Failed to load admin settings',
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
      }
    }

    loadAdminSettings()
  }, [toast])

  const passwordStrength = calculatePasswordStrength(formData.password)
  const strengthColor = getPasswordStrengthColor(passwordStrength)
  const strengthLabel = getPasswordStrengthLabel(passwordStrength)

  // Get selected plan details
  const selectedPlan = formData.planType ? pricingPlans.find(plan => {
    if (plan.type !== formData.planType) return false
    if (formData.planType === 'club' && plan.courts) {
      return plan.courts === formData.courts
    }
    return true
  }) : null
  const individualPlan = pricingPlans.find(plan => plan.type === 'individual')
  const clubPlan = pricingPlans.find(plan => plan.type === 'club')

  // Calculate prices with promo code discount
  const originalPrice = selectedPlan ? selectedPlan.price / 100 : 0
  const discountAmount = appliedPromoCode ? (originalPrice * appliedPromoCode.discountPercentage) / 100 : 0
  const finalPrice = originalPrice - discountAmount

  // Get trial duration text
  const getTrialText = () => {
    const trialDays = adminSettings?.trialDurationDays ?? 60;
    if (trialDays === 1) {
      return 'Choose a plan to start your 1-day FREE trial today!';
    } else if (trialDays === 30) {
      return 'Choose a plan to start your 1-month FREE trial today!';
    } else if (trialDays === 60) {
      return 'Choose a plan to start your 2-month FREE trial today!';
    } else if (trialDays === 90) {
      return 'Choose a plan to start your 3-month FREE trial today!';
    } else {
      return `Choose a plan to start your ${trialDays}-day FREE trial today!`;
    }
  };

  return (
    <Box maxW="2xl" mx="auto" bg={cardBg} rounded="lg" shadow="md" p={6} border="1px" borderColor={borderColor}>
      {/* Back Button */}
      <Box textAlign="center" mb={6}>
        <IconButton
          aria-label="Back to landing page"
          icon={<FaArrowLeft />}
          onClick={() => navigate('/')}
          variant="ghost"
          color="white"
          _hover={{ bg: 'rgba(255, 255, 255, 0.1)' }}
          size="lg"
        />
      </Box>
      <Text fontSize="2xl" fontWeight="bold" textAlign="center" mb={6} color={textColor}>
        Create Account
      </Text>
      
      <VStack as="form" onSubmit={handleSubmit} spacing={4}>
        {/* Subscription Plan */}
        <Box w="full" pt={4}>
          {/* <Text fontSize="lg" fontWeight="medium" color={textColor} mb={4}>Subscription Plan</Text> */}
          <Text fontSize="sm" color="gray.400" mb={4}>
            {getTrialText()}
          </Text>
          
          {isLoadingPricing ? (
            <Box textAlign="center" py={8}>
              <Text color="gray.400">Loading pricing information...</Text>
            </Box>
          ) : (
            <VStack spacing={4} w="full">
              {/* Plan Selection - Show selected plan or Choose Plan button */}
              {selectedPlan ? (
                <Box w="full" p={4} bg="blue.900" borderRadius="lg" border="1px solid" borderColor="blue.400">
                  <VStack spacing={2} align="start">
                    <HStack justify="space-between" w="full">
                      <Text fontWeight="bold" color="white">
                        Selected: {selectedPlan.name || `${formData.planType === 'individual' ? 'Individual' : 'Club'} Plan`}
                      </Text>
                      <Button
                        size="sm"
                        variant="ghost"
                        color="blue.300"
                        onClick={() => navigate('/pricing?from=register')}
                      >
                        Change Plan
                      </Button>
                    </HStack>
                    <Text fontSize="lg" fontWeight="bold" color="blue.300">
                      ZAR {finalPrice.toLocaleString()}
                      {formData.planType === 'club' ? '/court per month' : '/month'}
                      {appliedPromoCode && (
                        <Text as="span" fontSize="sm" color="green.400" ml={2}>
                          (Save {discountAmount.toLocaleString()})
                        </Text>
                      )}
                    </Text>
                    {appliedPromoCode && (
                      <Text fontSize="sm" color="gray.400" textDecoration="line-through">
                        Original: ZAR {originalPrice.toLocaleString()}
                        {formData.planType === 'club' ? '/court per month' : '/month'}
                      </Text>
                    )}
                    {formData.planType === 'club' && (
                      <Text fontSize="sm" color="gray.300">
                        {formData.courts || 0} court{(formData.courts || 0) > 1 ? 's' : ''} selected
                      </Text>
                    )}
                    {/* Total monthly amount */}
                    <Text fontSize="md" fontWeight="bold" color="white" mt={2}>
                      Total: ZAR {(() => {
                        // Always calculate based on current price and courts
                        const pricePerCourt = appliedPromoCode ? finalPrice : originalPrice;
                        const totalAmount = pricePerCourt * (formData.courts || 1);
                        return totalAmount.toFixed(2);
                      })()}/month
                    </Text>
                  </VStack>
                </Box>
              ) : (
                <VStack spacing={2} w="full">
                  <Button
                    size="lg"
                    variant="outline"
                    color="#3182ce"
                    borderColor="#3182ce"
                    _hover={{ 
                      bg: 'rgba(49, 130, 206, 0.1)',
                      borderColor: '#2c5aa0',
                      color: '#2c5aa0'
                    }}
                    onClick={() => navigate('/pricing?from=register')}
                    h="60px"
                    w="full"
                  >
                    Choose Plan<Text as="span" color="red.400">*</Text>
                  </Button>
                  <Text fontSize="xs" color="gray.400" textAlign="left">
                    Cancel anytime. <Text as="span" color="red.400">*Required.</Text>
                  </Text>
                </VStack>
              )}
            </VStack>
          )}
          


          {/* Promo Code Section */}
          <Box mt={4}>
            <FormControl>
              <HStack spacing={2}>
                <Input
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                  placeholder="Promo code (optional)"
                  bg={inputBg}
                  borderColor={inputBorder}
                  color="white"
                  _placeholder={{ color: placeholderColor }}
                  _focus={{ borderColor: '#3182ce', boxShadow: '0 0 0 1px #3182ce' }}
                  isDisabled={isValidatingPromoCode}
                />
                <Button
                  size="md"
                  colorScheme="blue"
                  onClick={handlePromoCodeValidation}
                  isLoading={isValidatingPromoCode}
                  loadingText="Validating..."
                  isDisabled={!promoCode.trim()}
                >
                  Apply
                </Button>
              </HStack>
              
              {appliedPromoCode && (
                <Box mt={3} p={3} bg="green.900" borderRadius="md" border="1px" borderColor="green.700">
                  <HStack justify="space-between">
                    <VStack align="start" spacing={1}>
                      <Text fontSize="sm" color="green.300" fontWeight="medium">
                        Promo Code Applied: {appliedPromoCode.code}
                      </Text>
                      <Text fontSize="sm" color="green.400">
                        {appliedPromoCode.discountPercentage}% discount
                      </Text>
                      <Text fontSize="sm" color="green.400">
                        Applies to: {(appliedPromoCode.monthlyPayments ?? 0) === 0 ? 'All payments' : `${appliedPromoCode.monthlyPayments ?? 0} month${(appliedPromoCode.monthlyPayments ?? 0) > 1 ? 's' : ''}`}
                      </Text>
                    </VStack>
                    <Button
                      size="sm"
                      variant="ghost"
                      color="green.300"
                      onClick={() => {
                        setAppliedPromoCode(null)
                        setPromoCode('')
                      }}
                    >
                      Remove
                    </Button>
                  </HStack>
                </Box>
              )}
            </FormControl>
          </Box>
        </Box>

        {/* Basic Information */}
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="full">
          <FormControl>
            <FormLabel color={labelColor} fontSize="sm" fontWeight="medium">
              Full Name *
            </FormLabel>
            <Input
              type="text"
              value={formData.displayName}
              onChange={(e) => handleInputChange('displayName', e.target.value)}
              required
              bg={inputBg}
              borderColor={inputBorder}
              color="white"
              _placeholder={{ color: placeholderColor }}
              _focus={{ borderColor: '#3182ce', boxShadow: '0 0 0 1px #3182ce' }}
              placeholder="Enter your full name"
            />
          </FormControl>
          
          <FormControl>
            <FormLabel color={labelColor} fontSize="sm" fontWeight="medium">
              Email Address *
            </FormLabel>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              required
              bg={inputBg}
              borderColor={inputBorder}
              color="white"
              _placeholder={{ color: placeholderColor }}
              _focus={{ borderColor: '#3182ce', boxShadow: '0 0 0 1px #3182ce' }}
              placeholder="Enter your email"
            />
          </FormControl>
        </SimpleGrid>

        {/* Password Fields */}
        <FormControl>
          <FormLabel color={labelColor} fontSize="sm" fontWeight="medium">
            Password *
          </FormLabel>
          <Box position="relative">
            <Input
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={(e) => handleInputChange('password', e.target.value)}
              required
              bg={inputBg}
              borderColor={inputBorder}
              color="white"
              _placeholder={{ color: placeholderColor }}
              _focus={{ borderColor: '#3182ce', boxShadow: '0 0 0 1px #3182ce' }}
              placeholder="Create a strong password"
              pr="3rem"
            />
            <IconButton
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              icon={<Icon as={showPassword ? FaEyeSlash : FaEye} />}
              size="sm"
              position="absolute"
              right={2}
              top="50%"
              transform="translateY(-50%)"
              variant="ghost"
              color="gray.400"
              _hover={{ bg: 'transparent' }}
              onClick={() => setShowPassword(!showPassword)}
            />
          </Box>
          
          {/* Password Strength Indicator */}
          {formData.password && (
            <Box mt={3}>
              <HStack justify="space-between" mb={2}>
                <Text fontSize="sm" color="gray.400">Password Strength:</Text>
                <Text fontSize="sm" color={strengthColor} fontWeight="medium">
                  {strengthLabel}
                </Text>
              </HStack>
              <Progress 
                value={passwordStrength} 
                colorScheme={passwordStrength < 25 ? 'red' : passwordStrength < 50 ? 'orange' : passwordStrength < 75 ? 'yellow' : 'green'}
                size="sm"
                borderRadius="md"
              />
            </Box>
          )}
          
          {/* Password Requirements */}
          {formData.password && (
            <Box mt={3} p={3} bg="gray.800" borderRadius="md" border="1px" borderColor="gray.700">
              <Text fontSize="sm" color="gray.300" mb={2} fontWeight="medium">
                Password Requirements:
              </Text>
              <VStack align="start" spacing={1}>
                {currentPasswordRequirements.map((req, index) => (
                  <HStack key={index} spacing={2}>
                    <Icon 
                      as={req.met ? FaCheck : FaTimes} 
                      color={req.met ? 'green.400' : 'red.400'} 
                      boxSize={3} 
                    />
                    <Text fontSize="xs" color={req.met ? 'green.400' : 'red.400'}>
                      {req.label}
                    </Text>
                  </HStack>
                ))}
              </VStack>
            </Box>
          )}
        </FormControl>
        
        <FormControl>
          <FormLabel color={labelColor} fontSize="sm" fontWeight="medium">
            Confirm Password *
          </FormLabel>
          <Box position="relative">
            <Input
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              bg={inputBg}
              borderColor={inputBorder}
              color="white"
              _placeholder={{ color: placeholderColor }}
              _focus={{ borderColor: '#3182ce', boxShadow: '0 0 0 1px #3182ce' }}
              placeholder="Confirm your password"
              pr="3rem"
            />
            <IconButton
              aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
              icon={<Icon as={showConfirmPassword ? FaEyeSlash : FaEye} />}
              size="sm"
              position="absolute"
              right={2}
              top="50%"
              transform="translateY(-50%)"
              variant="ghost"
              color="gray.400"
              _hover={{ bg: 'transparent' }}
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            />
          </Box>
          {confirmPassword && formData.password !== confirmPassword && (
            <Text color="red.400" fontSize="xs" mt={1}>Passwords do not match</Text>
          )}
        </FormControl>

        <Text 
          color={labelColor} 
          fontSize="sm" 
          fontWeight="bold" 
          textDecoration="underline"
          mt={2}
          mb={2}
        >
          Optional:
        </Text>

        <FormControl>
          <FormLabel color={labelColor} fontSize="sm" fontWeight="medium">
            Phone Number
          </FormLabel>
          <Input
            type="tel"
            value={formData.phoneNumber}
            onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
            bg={inputBg}
            borderColor={inputBorder}
            color="white"
            _placeholder={{ color: placeholderColor }}
            _focus={{ borderColor: '#3182ce', boxShadow: '0 0 0 1px #3182ce' }}
            placeholder="+27 12 345 6789"
          />
        </FormControl>

        {/* VAT Information */}
        <Box w="full" pt={4}>
          <HStack mb={4}>
            <Checkbox
              checked={showVatFields}
              onChange={(e) => setShowVatFields(e.target.checked)}
            />
            <Text color={labelColor} fontSize="sm" fontWeight="medium">
              Add VAT information for invoicing
            </Text>
          </HStack>
          
          {showVatFields && (
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="full">
              <FormControl>
                <FormLabel color={labelColor} fontSize="sm" fontWeight="medium">
                  Company Name
                </FormLabel>
                <Input
                  type="text"
                  value={formData.companyName}
                  onChange={(e) => handleInputChange('companyName', e.target.value)}
                  bg={inputBg}
                  borderColor={inputBorder}
                  color="white"
                  _placeholder={{ color: placeholderColor }}
                  _focus={{ borderColor: '#3182ce', boxShadow: '0 0 0 1px #3182ce' }}
                  placeholder="Company name"
                />
              </FormControl>
              
              <FormControl>
                <FormLabel color={labelColor} fontSize="sm" fontWeight="medium">
                  VAT Number
                </FormLabel>
                <Input
                  type="text"
                  value={formData.vatNumber}
                  onChange={(e) => handleInputChange('vatNumber', e.target.value)}
                  bg={inputBg}
                  borderColor={inputBorder}
                  color="white"
                  _placeholder={{ color: placeholderColor }}
                  _focus={{ borderColor: '#3182ce', boxShadow: '0 0 0 1px #3182ce' }}
                  placeholder="VAT registration number"
                />
              </FormControl>
            </SimpleGrid>
          )}
        </Box>

        {/* Address */}
        <Box w="full" pt={4}>
          <Text fontSize="lg" fontWeight="medium" color={textColor} mb={4}>Address</Text>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="full">
            <FormControl gridColumn={{ base: '1', md: '1 / -1' }}>
              <FormLabel color={labelColor} fontSize="sm" fontWeight="medium">
                Street Address
              </FormLabel>
              <Input
                type="text"
                value={formData.address.street}
                onChange={(e) => handleInputChange('address.street', e.target.value)}
                bg={inputBg}
                borderColor={inputBorder}
                color="white"
                _placeholder={{ color: placeholderColor }}
                _focus={{ borderColor: '#3182ce', boxShadow: '0 0 0 1px #3182ce' }}
                placeholder="Street address"
              />
            </FormControl>
            
            <FormControl>
              <FormLabel color={labelColor} fontSize="sm" fontWeight="medium">
                City
              </FormLabel>
              <Input
                type="text"
                value={formData.address.city}
                onChange={(e) => handleInputChange('address.city', e.target.value)}
                bg={inputBg}
                borderColor={inputBorder}
                color="white"
                _placeholder={{ color: placeholderColor }}
                _focus={{ borderColor: '#3182ce', boxShadow: '0 0 0 1px #3182ce' }}
                placeholder="City"
              />
            </FormControl>
            
            <FormControl>
              <FormLabel color={labelColor} fontSize="sm" fontWeight="medium">
                Postal Code
              </FormLabel>
              <Input
                type="text"
                value={formData.address.postalCode}
                onChange={(e) => handleInputChange('address.postalCode', e.target.value)}
                bg={inputBg}
                borderColor={inputBorder}
                color="white"
                _placeholder={{ color: placeholderColor }}
                _focus={{ borderColor: '#3182ce', boxShadow: '0 0 0 1px #3182ce' }}
                placeholder="Postal code"
              />
            </FormControl>
          </SimpleGrid>
        </Box>

        {error && (
          <Box color="red.400" fontSize="sm" bg="red.900" opacity={0.2} p={3} rounded="md" border="1px" borderColor="red.800" w="full">
            {error}
          </Box>
        )}
        
        <Button
          type="submit"
          w="full"
          bg="#3182ce"
          color="white"
          _hover={{ bg: '#2c5aa0' }}
          isDisabled={loading || !validateForm()}
          opacity={loading || !validateForm() ? 0.5 : 1}
        >
          {loading ? 'Creating Account...' : 'Create Account'}
        </Button>
        
        {onSwitchToLogin && (
          <Box textAlign="center">
            <Text color="gray.400" fontSize="sm">
              Already have an account?{' '}
              <Button
                variant="link"
                color="blue.400"
                _hover={{ color: 'blue.300' }}
                fontWeight="medium"
                onClick={onSwitchToLogin}
              >
                Log in
              </Button>
            </Text>
          </Box>
        )}
      </VStack>
    </Box>
  )
} 